
<script>

  import { useEventBus } from '@vueuse/core';

  const stop = useEventBus( 'stop' );

</script>

<script setup>

  import { v4 as uuidv4 } from 'uuid';

  import { ref, watch, onBeforeUnmount } from 'vue';

  import { useEventListener, useMouseInElement } from '@vueuse/core';

  import { PlayIcon, PauseIcon } from '@heroicons/vue/24/solid';

  import { formatTime } from '../utils.js';

  import Waveform from './waveform.vue';

  import Transcript from './transcript.vue';

  const id = uuidv4();

  const props = defineProps( { src: String, track: String } );

  const audio = ref( null );

  const scrubber = ref( null );

  const playing = ref( false );

  const time = ref( 0 );

  const duration = ref( 0 );

  const scrubbing = ref( false );

  const cues = ref( null );

  const unsubscribe = stop.on( from => {
  
    if ( from !== id ) audio.value.pause();
  
  } );

  onBeforeUnmount( () => unsubscribe() );

  useEventListener( 'mouseup', () => scrubbing.value = false );

  const { elementX, elementWidth } = useMouseInElement( scrubber );

  function onEnded() {

    playing.value = false;

    audio.value.currentTime = 0;

  }

  watch( [ scrubbing, elementX ], () => {

    const progress = Math.max( 0, Math.min( 1, elementX.value / elementWidth.value ) );

    if ( scrubbing.value ) audio.value.currentTime = progress * duration.value;

  } );

</script>

<template>

  <div>

    <div class="flex items-center gap-6">

      <audio ref="audio" :src="src" playsinline

        @play="stop.emit( id ); playing = true" @pause="playing = false" @ended="onEnded"
        
        @timeupdate = "time = audio?.currentTime" @durationchange ="duration = audio?.duration">

        <track kind="captions" default :src="track" @load="event => cues = Array.from( event.target.track.cues )" />

      </audio>

      <div>

        <button class="flex items-center justify-center w-12 h-12 rounded-full border-2 border-black"
        
          @click="audio.paused ? audio.play() : audio.pause()" type="button" aria-label="play or pause audio">
          
          <PlayIcon class="w-5" v-if="! playing" />

          <PauseIcon class="w-5" v-if="playing" />
        
        </button>

        <div class="mt-2 text-center text-sm">{{ formatTime( time ) }}</div>

      </div>

      <div ref="scrubber" class="grow grid" @mousedown="scrubbing = true" aria-label="audio time scrubber">

        <Waveform class="row-start-1 col-start-1" :src="src" />

        <div class="row-start-1 col-start-1">

          <div class="h-full border-r border-current bg-black/25" :style="{ width: `${ time / duration * 100 }%` }" />

        </div>

      </div>

    </div>
    
    <div class="flex items-center gap-4 mt-4" aria-hidden="true" v-if="track">

      <Transcript :cues="cues" />

    </div>

  </div>

</template>
